import { createServerFn } from "@tanstack/react-start";
import { and, asc, eq, inArray } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { mediaIdSchema, resolveImageMediaInput } from "../media/input.server";
import { invalidateMarketingCache } from "./public.functions";

const statusSchema = z.enum(["draft", "published", "archived"]);

const whoWeServeInput = z.object({
  name: z.string().min(1),
  body: z.string().optional(),
  imageMediaId: mediaIdSchema,
  imageUrl: z.string().optional().nullable(),
  status: statusSchema.default("published"),
});

const testimonialInput = z.object({
  quote: z.string().min(1),
  name: z.string().min(1),
  role: z.string().min(1),
  imageMediaId: mediaIdSchema,
  imageUrl: z.string().optional().nullable(),
  status: statusSchema.default("published"),
});

async function nextSortOrder(table: typeof schema.whoWeServeItems | typeof schema.testimonials) {
  const rows = await getDb().select({ sortOrder: table.sortOrder }).from(table);
  return rows.length ? Math.max(...rows.map((r) => r.sortOrder)) + 1 : 0;
}

export const listWhoWeServeAdmin = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("pages", "read");
  return getDb()
    .select()
    .from(schema.whoWeServeItems)
    .orderBy(asc(schema.whoWeServeItems.sortOrder), asc(schema.whoWeServeItems.name));
});

export const getWhoWeServeAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("pages", "read");
    const [row] = await getDb()
      .select()
      .from(schema.whoWeServeItems)
      .where(eq(schema.whoWeServeItems.id, data.id))
      .limit(1);
    return row ?? null;
  });

export const createWhoWeServeItem = createServerFn({ method: "POST" })
  .inputValidator(whoWeServeInput)
  .handler(async ({ data }) => {
    const user = await requirePermission("pages", "write");
    const image = await resolveImageMediaInput(data);
    const [row] = await getDb()
      .insert(schema.whoWeServeItems)
      .values({
        name: data.name,
        body: data.body,
        imageMediaId: image.imageMediaId,
        imageUrl: image.imageUrl,
        sortOrder: await nextSortOrder(schema.whoWeServeItems),
        status: data.status,
        publishedAt: data.status === "published" ? new Date() : null,
        createdBy: user.id,
        updatedBy: user.id,
      })
      .returning();
    await invalidateMarketingCache();
    return row;
  });

export const updateWhoWeServeItem = createServerFn({ method: "POST" })
  .inputValidator(whoWeServeInput.extend({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const user = await requirePermission("pages", "write");
    const db = getDb();
    const [existing] = await db
      .select()
      .from(schema.whoWeServeItems)
      .where(eq(schema.whoWeServeItems.id, data.id))
      .limit(1);
    if (!existing) throw new Error("Not found");

    const image = await resolveImageMediaInput(data);
    const [row] = await db
      .update(schema.whoWeServeItems)
      .set({
        name: data.name,
        body: data.body,
        imageMediaId: image.imageMediaId,
        imageUrl: image.imageUrl,
        status: data.status,
        publishedAt:
          data.status === "published" ? (existing.publishedAt ?? new Date()) : existing.publishedAt,
        updatedBy: user.id,
        updatedAt: new Date(),
      })
      .where(eq(schema.whoWeServeItems.id, data.id))
      .returning();
    await invalidateMarketingCache();
    return row;
  });

export const deleteWhoWeServeItem = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("pages", "delete");
    await getDb().delete(schema.whoWeServeItems).where(eq(schema.whoWeServeItems.id, data.id));
    await invalidateMarketingCache();
    return { ok: true };
  });

export const reorderWhoWeServeItems = createServerFn({ method: "POST" })
  .inputValidator(z.object({ ids: z.array(z.string().uuid()).min(1) }))
  .handler(async ({ data }) => {
    await requirePermission("pages", "write");
    const db = getDb();
    for (let i = 0; i < data.ids.length; i++) {
      await db
        .update(schema.whoWeServeItems)
        .set({ sortOrder: i, updatedAt: new Date() })
        .where(and(eq(schema.whoWeServeItems.id, data.ids[i]), inArray(schema.whoWeServeItems.id, data.ids)));
    }
    await invalidateMarketingCache();
    return { ok: true };
  });

export const listTestimonialsAdmin = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("pages", "read");
  return getDb()
    .select()
    .from(schema.testimonials)
    .orderBy(asc(schema.testimonials.sortOrder), asc(schema.testimonials.name));
});

export const getTestimonialAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("pages", "read");
    const [row] = await getDb()
      .select()
      .from(schema.testimonials)
      .where(eq(schema.testimonials.id, data.id))
      .limit(1);
    return row ?? null;
  });

export const createTestimonial = createServerFn({ method: "POST" })
  .inputValidator(testimonialInput)
  .handler(async ({ data }) => {
    const user = await requirePermission("pages", "write");
    const image = await resolveImageMediaInput(data);
    const [row] = await getDb()
      .insert(schema.testimonials)
      .values({
        quote: data.quote,
        name: data.name,
        role: data.role,
        imageMediaId: image.imageMediaId,
        imageUrl: image.imageUrl,
        sortOrder: await nextSortOrder(schema.testimonials),
        status: data.status,
        publishedAt: data.status === "published" ? new Date() : null,
        createdBy: user.id,
        updatedBy: user.id,
      })
      .returning();
    await invalidateMarketingCache();
    return row;
  });

export const updateTestimonial = createServerFn({ method: "POST" })
  .inputValidator(testimonialInput.extend({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const user = await requirePermission("pages", "write");
    const db = getDb();
    const [existing] = await db
      .select()
      .from(schema.testimonials)
      .where(eq(schema.testimonials.id, data.id))
      .limit(1);
    if (!existing) throw new Error("Not found");

    const image = await resolveImageMediaInput(data);
    const [row] = await db
      .update(schema.testimonials)
      .set({
        quote: data.quote,
        name: data.name,
        role: data.role,
        imageMediaId: image.imageMediaId,
        imageUrl: image.imageUrl,
        status: data.status,
        publishedAt:
          data.status === "published" ? (existing.publishedAt ?? new Date()) : existing.publishedAt,
        updatedBy: user.id,
        updatedAt: new Date(),
      })
      .where(eq(schema.testimonials.id, data.id))
      .returning();
    await invalidateMarketingCache();
    return row;
  });

export const deleteTestimonial = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("pages", "delete");
    await getDb().delete(schema.testimonials).where(eq(schema.testimonials.id, data.id));
    await invalidateMarketingCache();
    return { ok: true };
  });

export const reorderTestimonials = createServerFn({ method: "POST" })
  .inputValidator(z.object({ ids: z.array(z.string().uuid()).min(1) }))
  .handler(async ({ data }) => {
    await requirePermission("pages", "write");
    const db = getDb();
    for (let i = 0; i < data.ids.length; i++) {
      await db
        .update(schema.testimonials)
        .set({ sortOrder: i, updatedAt: new Date() })
        .where(and(eq(schema.testimonials.id, data.ids[i]), inArray(schema.testimonials.id, data.ids)));
    }
    await invalidateMarketingCache();
    return { ok: true };
  });
